/**
 * 机上調査の地点の一覧 (ファイルビューア) と解析 / 再解析の対象の選択 (docs/27 §7 / §8)。
 *
 * 2026-09-16 (オーナー指示): 登録済み 360度写真だけでなく **Google ストリートビュー由来の地点も**
 * 一覧・選択・一括再解析の対象にする (以前は 1 件ずつしか再解析できなかった)。画像の出所でも絞り込める。
 *
 * - 一覧: サムネ / ファイル名 / 撮影時刻 / 解析状態。ファイル名 (部分一致)・撮影日 (範囲)・解析状態・
 *   画像の出所で絞り込み。
 * - 選択: 行チェック、絞り込み結果の全選択 / 解除。地図の多角形選択は親 (StreetSurveyPage) が
 *   同じ選択集合に流し込む。
 * - 操作: 選択した地点の AI 解析 / 路線として登録 / エリアとして登録 / 削除。
 *   路線・エリアは未解析の地点を含むと登録できない (集計値が保存時点で固定されるため、docs/27 §8)。
 */
import { Button, Card, Checkbox } from '@citizen-walk/shared-ui';
import { useMemo, useState } from 'react';
import type { StreetSurveyAnalysisStatus, StreetSurveyPost } from './useStreetSurveyPosts';
import { useStoragePhotoUrl } from './useStoragePhotoUrl';

export type LibraryStatusFilter = 'all' | 'none' | 'done' | 'failed' | 'pending';

const STATUS_LABELS: Record<StreetSurveyAnalysisStatus, string> = {
  none: '未解析',
  pending: '解析中',
  done: '解析済',
  failed: '失敗',
};

const STATUS_CLASS: Record<StreetSurveyAnalysisStatus, string> = {
  none: 'bg-slate-200 text-slate-700',
  pending: 'bg-warning-100 text-warning-800',
  done: 'bg-primary-100 text-primary-800',
  failed: 'bg-danger-100 text-danger-800',
};

/** 画像の出所の絞り込み (docs/27 §5)。 */
export type LibraryProviderFilter = 'all' | 'upload-360' | 'google';

export interface LibraryFilter {
  name: string;
  from: string;
  to: string;
  status: LibraryStatusFilter;
  provider: LibraryProviderFilter;
}

const EMPTY_FILTER: LibraryFilter = {
  name: '',
  from: '',
  to: '',
  status: 'all',
  provider: 'all',
};

/** フィルタ適用 (pure、テスト容易性のため export)。 */
export function applyLibraryFilter(
  posts: StreetSurveyPost[],
  f: LibraryFilter,
): StreetSurveyPost[] {
  const name = f.name.trim().toLowerCase();
  const from = f.from ? new Date(`${f.from}T00:00:00`) : null;
  const to = f.to ? new Date(`${f.to}T23:59:59.999`) : null;
  return posts.filter((p) => {
    // ファイル名は 360度写真のみ持つ。Google 由来の地点は地点 ID でも引けるようにする。
    if (name && !`${p.fileName ?? ''} ${p.id}`.toLowerCase().includes(name)) return false;
    if (f.status !== 'all' && p.analysisStatus !== f.status) return false;
    if (f.provider && f.provider !== 'all' && p.provider !== f.provider) return false;
    const t = p.capturedAt ?? p.createdAt;
    if (from && (!t || t < from)) return false;
    if (to && (!t || t > to)) return false;
    return true;
  });
}

function formatDateTime(d: Date | null): string {
  return d ? d.toLocaleString('ja-JP') : '—';
}

function Thumb({ post }: { post: StreetSurveyPost }) {
  const thumb = useStoragePhotoUrl(post.photoThumbPath ?? post.photoPath, post.photoThumbUrl);
  if (post.provider === 'google') {
    // Google ストリートビューの静止画は保存しない (ToS、docs/27 §9-1) ためサムネは無い。
    return (
      <span className="flex h-10 w-16 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] font-semibold text-slate-500">
        ストリート
        <br />
        ビュー
      </span>
    );
  }
  return (
    <span className="relative block w-16 h-10 rounded overflow-hidden bg-slate-200 shrink-0">
      {thumb.status === 'ready' ? (
        <img src={thumb.url} alt="" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <span className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">
          {thumb.status === 'error' ? '×' : '…'}
        </span>
      )}
      <span className="absolute left-0.5 bottom-0.5 rounded bg-black/60 px-0.5 text-[9px] font-bold text-white">
        360°
      </span>
    </span>
  );
}

export interface StreetSurveyPhotoLibraryPanelProps {
  /** 机上調査の地点 (登録した 360度写真と Google ストリートビュー由来の両方)。 */
  posts: StreetSurveyPost[];
  selectedIds: ReadonlySet<string>;
  onSelectionChange: (next: Set<string>) => void;
  /** 行クリックで地図上の選択 (プレビュー) を切り替える。 */
  onPreview: (postId: string) => void;
  previewPostId: string | null;
  canRun: boolean;
  onAnalyze: (posts: StreetSurveyPost[]) => void;
  onRegisterRoute: (posts: StreetSurveyPost[]) => void;
  onRegisterArea: (posts: StreetSurveyPost[]) => void;
  onDelete: (posts: StreetSurveyPost[]) => void;
  /** 地図の多角形選択モードの開始 / 終了。 */
  mapSelectActive: boolean;
  onToggleMapSelect: () => void;
  busy?: boolean;
}

export function StreetSurveyPhotoLibraryPanel({
  posts,
  selectedIds,
  onSelectionChange,
  onPreview,
  previewPostId,
  canRun,
  onAnalyze,
  onRegisterRoute,
  onRegisterArea,
  onDelete,
  mapSelectActive,
  onToggleMapSelect,
  busy = false,
}: StreetSurveyPhotoLibraryPanelProps) {
  const [filter, setFilter] = useState<LibraryFilter>(EMPTY_FILTER);
  const [collapsed, setCollapsed] = useState(false);

  const filtered = useMemo(() => {
    const list = applyLibraryFilter(posts, filter);
    return [...list].sort((a, b) => {
      const ta = (a.capturedAt ?? a.createdAt)?.getTime() ?? 0;
      const tb = (b.capturedAt ?? b.createdAt)?.getTime() ?? 0;
      if (ta !== tb) return ta - tb;
      return (a.fileName ?? '').localeCompare(b.fileName ?? '', 'ja', { numeric: true });
    });
  }, [posts, filter]);

  const selectedPosts = useMemo(
    () => posts.filter((p) => selectedIds.has(p.id)),
    [posts, selectedIds],
  );
  const unanalyzedSelected = selectedPosts.filter((p) => p.analysisStatus !== 'done').length;
  /** 選択のうち解析済み = 再解析になる件数 (課金が発生するので件数を明示する)。 */
  const analyzedSelected = selectedPosts.length - unanalyzedSelected;
  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));

  const counts = useMemo(() => {
    const c: Record<StreetSurveyAnalysisStatus, number> = {
      none: 0,
      pending: 0,
      done: 0,
      failed: 0,
    };
    for (const p of posts) c[p.analysisStatus]++;
    return c;
  }, [posts]);

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  }

  function selectAllFiltered(select: boolean) {
    const next = new Set(selectedIds);
    for (const p of filtered) {
      if (select) next.add(p.id);
      else next.delete(p.id);
    }
    onSelectionChange(next);
  }

  return (
    <Card padding="md" className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-slate-700">
          机上調査の地点（{posts.length} 件: 未解析 {counts.none} / 解析済 {counts.done}
          {counts.failed > 0 ? ` / 失敗 ${counts.failed}` : ''}
          {counts.pending > 0 ? ` / 解析中 ${counts.pending}` : ''}）
        </h2>
        <button
          type="button"
          className="text-xs text-primary-600 hover:underline"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? '一覧を開く' : '一覧をたたむ'}
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        登録した360°写真と、ストリートビューで解析した地点の一覧です。地点を選んで「AI解析」を実行すると、
        解析済みの地点は<strong>そのときの設定で解析し直します</strong>
        （前の結果は解析の履歴に残ります）。
        行をクリックすると地図で位置を示し、右側で画像を見回せます。
      </p>

      {!collapsed && (
        <>
          {/* フィルタ */}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-5">
            <label className="block text-xs text-slate-600">
              ファイル名・地点 ID
              <input
                type="text"
                value={filter.name}
                onChange={(e) => setFilter((f) => ({ ...f, name: e.target.value }))}
                placeholder="例: R0010"
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              撮影日（から）
              <input
                type="date"
                value={filter.from}
                onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))}
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              撮影日（まで）
              <input
                type="date"
                value={filter.to}
                onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))}
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              解析状態
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, status: e.target.value as LibraryStatusFilter }))
                }
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
                style={{ maxWidth: '100%' }}
              >
                <option value="all">すべて</option>
                <option value="none">未解析</option>
                <option value="done">解析済</option>
                <option value="failed">失敗</option>
                <option value="pending">解析中</option>
              </select>
            </label>
            <label className="block text-xs text-slate-600">
              画像の出所
              <select
                value={filter.provider}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, provider: e.target.value as LibraryProviderFilter }))
                }
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
                style={{ maxWidth: '100%' }}
              >
                <option value="all">すべて</option>
                <option value="upload-360">登録した360°写真</option>
                <option value="google">ストリートビュー</option>
              </select>
            </label>
          </div>

          {/* 選択操作 */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => selectAllFiltered(!allFilteredSelected)}
              disabled={filtered.length === 0}
            >
              {allFilteredSelected
                ? '絞り込み結果の選択を解除'
                : `絞り込み結果を全選択（${filtered.length}）`}
            </Button>
            <Button
              size="sm"
              variant={mapSelectActive ? 'primary' : 'secondary'}
              onClick={onToggleMapSelect}
            >
              {mapSelectActive ? '地図での範囲選択をやめる' : '地図で範囲を選んで追加'}
            </Button>
            {selectedIds.size > 0 && (
              <Button size="sm" variant="secondary" onClick={() => onSelectionChange(new Set())}>
                選択をすべて解除
              </Button>
            )}
            <span className="text-xs text-slate-600">選択中: {selectedIds.size} 件</span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={!canRun || selectedPosts.length === 0 || busy}
              onClick={() => onAnalyze(selectedPosts)}
            >
              選択した {selectedPosts.length} 件をAI解析
              {analyzedSelected > 0 ? `（うち ${analyzedSelected} 件は再解析）` : ''}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={selectedPosts.length === 0 || unanalyzedSelected > 0 || busy}
              onClick={() => onRegisterRoute(selectedPosts)}
            >
              路線として登録
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={selectedPosts.length === 0 || unanalyzedSelected > 0 || busy}
              onClick={() => onRegisterArea(selectedPosts)}
            >
              エリアとして登録
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={selectedPosts.length === 0 || busy}
              onClick={() => onDelete(selectedPosts)}
            >
              選択を削除
            </Button>
            {!canRun && (
              <span className="text-xs text-warning-700">
                AI解析の実行はマスター管理者（または許可された地区管理者）のみ可能です。
              </span>
            )}
            {selectedPosts.length > 0 && unanalyzedSelected > 0 && (
              <span className="text-xs text-warning-700">
                未解析の地点が {unanalyzedSelected}{' '}
                件あります。路線・エリアとして登録するには先に解析してください。
              </span>
            )}
          </div>

          {/* 一覧 */}
          <div className="mt-3 max-h-80 overflow-auto rounded border border-slate-200">
            {filtered.length === 0 ? (
              <p className="p-3 text-sm text-slate-400">
                {posts.length === 0
                  ? 'まだ机上調査の地点がありません。'
                  : '絞り込み条件に一致する地点がありません。'}
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-2 py-1 w-8">
                      <Checkbox
                        label=""
                        checked={allFilteredSelected}
                        onChange={() => selectAllFiltered(!allFilteredSelected)}
                        aria-label="絞り込み結果を全選択"
                      />
                    </th>
                    <th className="px-2 py-1">写真</th>
                    <th className="px-2 py-1">ファイル名</th>
                    <th className="px-2 py-1">撮影時刻</th>
                    <th className="px-2 py-1">状態</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const checked = selectedIds.has(p.id);
                    const isPreview = p.id === previewPostId;
                    return (
                      <tr
                        key={p.id}
                        className={`border-t border-slate-100 cursor-pointer ${
                          isPreview
                            ? 'bg-primary-50'
                            : checked
                              ? 'bg-slate-50'
                              : 'hover:bg-slate-50'
                        }`}
                        onClick={() => onPreview(p.id)}
                      >
                        <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            label=""
                            checked={checked}
                            onChange={() => toggle(p.id)}
                            aria-label={`${p.fileName ?? p.id} を選択`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Thumb post={p} />
                        </td>
                        <td className="px-2 py-1 max-w-[12rem] truncate" title={p.fileName ?? ''}>
                          {p.fileName ??
                            (p.provider === 'google'
                              ? 'ストリートビューの地点'
                              : '(ファイル名なし)')}
                          {p.title ? (
                            <span className="block text-slate-400 truncate">{p.title}</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {formatDateTime(p.capturedAt ?? p.createdAt)}
                        </td>
                        <td className="px-2 py-1">
                          <span
                            className={`rounded px-1.5 py-0.5 ${STATUS_CLASS[p.analysisStatus]}`}
                          >
                            {STATUS_LABELS[p.analysisStatus]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
